package com.tovtalk.data.remote

import com.tovtalk.data.remote.dto.ApiErrorBody
import com.tovtalk.data.remote.dto.V2Response
import com.tovtalk.domain.entity.DomainResult
import com.tovtalk.utils.logger.Logger
import io.ktor.client.call.body
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.bodyAsText
import io.ktor.http.HttpStatusCode
import io.ktor.http.isSuccess
import kotlinx.serialization.json.Json

private val errorJson = Json { ignoreUnknownKeys = true; isLenient = true }

/**
 * Centralized response handling for v2 endpoints.
 *
 * Executes [request], validates the HTTP status, unwraps the [V2Response]
 * envelope, surfaces server [errors] (with their stable `code`) as
 * [DomainResult.Error], and funnels every thrown exception into a
 * [DomainResult.Error]. The result shape is identical to what the repositories
 * already produce by hand, so callers keep their existing public contracts.
 *
 * @param request executes the HTTP call and returns the raw [HttpResponse].
 * @param map transforms the unwrapped, non-null [T] payload into the domain [R].
 * @param emptyError message used when the envelope carries neither errors nor data.
 */
suspend inline fun <reified T, R> apiCall(
    request: () -> HttpResponse,
    crossinline map: (T) -> R,
    emptyError: String = "No data",
): DomainResult<R> {
    return try {
        val response = request()

        if (!response.status.isSuccess()) {
            return errorFromStatus(response)
        }

        val body = response.body<V2Response<T>>()
        body.errors?.takeIf { it.isNotEmpty() }?.let { errors ->
            val first = errors.first()
            return DomainResult.Error(first.message, errorCode = first.code)
        }

        val data = body.data ?: return DomainResult.Error(emptyError)
        DomainResult.Success(map(data))
    } catch (e: Exception) {
        DomainResult.Error("Network error: ${e.message}", e)
    }
}

/**
 * Builds a typed [DomainResult.Error] from a non-2xx response, preferring the
 * server's structured error body (`detail` + `code`) and falling back to the
 * HTTP status when the body can't be parsed.
 */
suspend fun errorFromStatus(response: HttpResponse): DomainResult.Error {
    return try {
        val raw = response.bodyAsText()
        val parsed = raw.takeIf { it.isNotBlank() }
            ?.let { runCatching { errorJson.decodeFromString(ApiErrorBody.serializer(), it) }.getOrNull() }
        // For 401, surface the server's structured error code/message (e.g.
        // account_locked / token_revoked) so the UI can distinguish a real
        // account problem from a plain expiry. Fall back to "Unauthorized"
        // when the body carries no parseable detail.
        if (response.status == HttpStatusCode.Unauthorized) {
            val message = parsed?.detail ?: "Unauthorized"
            return DomainResult.Error(message, errorCode = parsed?.code)
        }
        val message = parsed?.detail
            ?: raw.takeIf { it.isNotBlank() }
            ?: "Server error: ${response.status.value}"
        DomainResult.Error(message, errorCode = parsed?.code)
    } catch (e: Exception) {
        Logger.w("errorFromStatus parse failed: ${e.message}", "ApiCall")
        if (response.status == HttpStatusCode.Unauthorized) {
            DomainResult.Error("Unauthorized")
        } else {
            DomainResult.Error("Server error: ${response.status.value}")
        }
    }
}
